/**
 * Cards Design - Handles rendering of card formats for spot popups
 * Supports cards with title, description, CTA, and badges
 * Also handles badge design, tab design, and container HTML building
 */

import { LOGGING, CARDS_SETTINGS, MAP_ANIMATION_SETTINGS, CARDS_HEADER_CENTER_TABS, CARDS_HEADER_USE_TAG_ICON } from '/settings.js';
import { trackAdEvent } from '/app_core/ads/analytics.js';
import { getBadgeText, getCtaText, renderBadgeWithIcon, getBadgeColors } from '/app_core/src/cards/cards-translations.js';
import { getCurrentLanguage } from '../translations/translations.js';
import { renderSpotDetailCard } from '../marker/MarkerCards.js';
import { createIconMarkup } from '../utils/icon.js';
import { replaceLinkShortcodes } from '../utils/link-shortcodes.js';

// Helper function for cards logging
function logCards(message, data = null) {
    if (LOGGING.ENABLED && LOGGING.ADS) {
        if (data) {
            console.log(`[CARDS] ${message}`, data);
        } else {
            console.log(`[CARDS] ${message}`);
        }
    }
}

/**
 * Render a card for a spot
 */
export async function renderCard(card) {
    if (!card) {
        console.warn('No card data provided');
        return '';
    }

    if (card.card_type === 'spot-detail') {
        return await renderSpotDetailCard(card);
    }

    // Get current language - try multiple methods
    let currentLang = 'en';

    // Method 1: Try localStorage directly (same as translations.js)
    try {
        const storedLang = localStorage.getItem('language');
        if (storedLang && ['en', 'es', 'de'].includes(storedLang)) {
            currentLang = storedLang;
        }
    } catch (error) {
        console.warn('Could not read language from localStorage:', error);
    }

    // Method 2: Try translations module if available
    if (currentLang === 'en') {
        try {
            const { getCurrentLanguage } = await import('../translations/translations.js');
            currentLang = getCurrentLanguage();
        } catch (error) {
            console.warn('Could not load translations module:', error);
        }
    }

    // Method 3: Fallback to document lang attribute
    if (currentLang === 'en') {
        currentLang = document.documentElement.lang || 'en';
    }

    // Map the language to the correct suffix for database fields
    const langSuffix = currentLang === 'es' ? 'es' : currentLang === 'de' ? 'de' : 'en';

    // Get localized name, description and URL
    const name = card[`name_${langSuffix}`] || card.name_en;
    let description = card[`description_${langSuffix}`] || card.description_en;
    const url = card[`url_${langSuffix}`] || card.url_en;

    // Clean up description HTML - remove extra newlines/whitespace but preserve HTML tags
    if (description) {
        // Remove newlines and normalize whitespace, but preserve HTML structure
        // Replace newlines with spaces, then collapse multiple spaces to single space
        description = description
            .replace(/\r\n/g, ' ')  // Windows line breaks
            .replace(/\n/g, ' ')    // Unix line breaks
            .replace(/\r/g, ' ')    // Old Mac line breaks
            .replace(/\s+/g, ' ')   // Collapse multiple spaces to single space
            .trim();

        description = replaceLinkShortcodes(description);

        // Process HTML links: ensure they have target="_blank" and rel="noopener noreferrer"
        // Use a temporary DOM element to parse and modify HTML safely
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = description;
        const links = tempDiv.querySelectorAll('a');
        links.forEach(link => {
            link.setAttribute('target', '_blank');
            link.setAttribute('rel', 'noopener noreferrer');
            // Ensure links are inline (not block)
            link.style.display = 'inline';
        });
        description = tempDiv.innerHTML;
    }

    // Debug logging
    logCards('Cards language debug:', {
        currentLang,
        langSuffix,
        name_en: card.name_en,
        name_es: card.name_es,
        name_selected: name,
        description_en: card.description_en,
        description_es: card.description_es,
        description_selected: description
    });

    // Get translated text from cards-translations.js (SINGLE SOURCE OF TRUTH)
    // No fallback - if cta_text is empty, ctaText will be empty string (no CTA shown)
    const ctaText = card.cta_text ? getCtaText(card.cta_text, currentLang) : '';
    const badgeText = card.badge ? getBadgeText(card.badge, currentLang) : 'Info';

    // Build badge HTML inline (not from window variable)
    let badgeHtml = '';
    if (card.badge) {
        const badgeKey = String(card.badge).trim().toLowerCase();
        const badgeColors = getBadgeColors(badgeKey);
        const badgeStyle = badgeColors ? `style="background-color: ${badgeColors.backgroundColor}; color: ${badgeColors.textColor};"` : '';
        badgeHtml = `<span class="cards-badge" ${badgeStyle}><svg class="cards-badge-icon" aria-hidden="true" role="img"><use href="/app_core/src/cards/icons/cards-icons.svg#eye-solid-full"></use></svg> ${badgeText.toUpperCase()}</span>`;
    }

    const designClass = `cards-card`;
    const clickHandler = `if(typeof trackAdEvent==='function')trackAdEvent('click',{type:'card',id:'${card.id}',card:${JSON.stringify(card).replace(/"/g, '&quot;')}});`;

    return await renderCardContent(card, designClass, clickHandler, name, description, url, ctaText, badgeHtml);
}

/**
 * Render card content
 */
async function renderCardContent(card, designClass, clickHandler, name, description, url, ctaText, badgeHtml) {
    // Get banner display settings to check if title should be shown
    // Use domain settings directly - default to showing title if not specified
    let showTitle = true; // Default to true for backward compatibility
    try {
        const { getCardsSettings } = await import('/app_core/src/cards/cards-settings.js');
        const settings = await getCardsSettings();
        const bannerDisplay = settings.BANNER_DISPLAY;
        // If BANNER_DISPLAY exists and SHOW_TITLE is explicitly false, hide it
        // Otherwise, show it (default behavior)
        if (bannerDisplay && bannerDisplay.SHOW_TITLE === false) {
            showTitle = false;
        }
    } catch (error) {
        console.warn('Could not load banner display settings, defaulting to show title:', error);
    }

    // Clean content only - no close button, no badge (those are at container level)
    // Only show CTA if ctaText is not empty
    const ctaLink = ctaText ? `<a href="${url}" target="_blank" rel="noopener noreferrer" onclick="${clickHandler}" class="cards-cta-button-inline">${ctaText}</a>` : '';

    // Dynamic font size for long title (mobile only)
    let titleClass = 'cards-title';
    if (name && name.length > 30) {
        titleClass += ' cards-title-long-mobile';
    }

    return `
        <div class="${designClass}" data-card-type="card" data-card-id="${card.id}" data-partner="${card.partner || 'OTHER'}">
            ${showTitle ? `<div class="card-header">
                <h4 class="${titleClass}">${name}</h4>
            </div>` : ''}
            <div class="card-content">
                ${description || ctaLink ? `<p class="cards-description">${badgeHtml}${description}${ctaLink ? ' ' + ctaLink : ''}</p>` : ''}
            </div>
        </div>
    `;
}

// ===== BADGE DESIGN FUNCTIONS =====

/**
 * Get badge HTML for a card
 * @param {Object} card - Card object
 * @param {Object} bannerDisplay - Banner display settings
 * @returns {string} Badge HTML
 */
export function getBadgeHtmlForCard(card, bannerDisplay) {
    if (!bannerDisplay.SHOW_BADGE || !card || !card.badge) {
        return '';
    }

    const lang = getCurrentLanguage();
    const badgeKey = String(card.badge).trim().toLowerCase();
    const badgeTextRaw = getBadgeText(badgeKey, lang);
    const badgeText = badgeTextRaw ? badgeTextRaw.toUpperCase() : '';

    // Get badge suffix if available
    let badgeSuffix = '';
    if (typeof window.getBadgeSuffix === 'function') {
        badgeSuffix = window.getBadgeSuffix();
    }

    return renderBadgeWithIcon(badgeText + badgeSuffix, badgeKey, 'cards-badge-icon');
}

/**
 * Get badge color attributes (style and data attributes)
 * @param {string} badgeKey - Badge key
 * @returns {Object} Object with style, data, and colors properties
 */
function getBadgeColorAttributes(badgeKey) {
    const colors = getBadgeColors(badgeKey);
    if (!colors) return { style: '', data: '', colors: null };

    const styleParts = [];
    if (colors.backgroundColor) {
        styleParts.push(`background-color: ${colors.backgroundColor}`);
    }
    if (colors.textColor) {
        styleParts.push(`color: ${colors.textColor}`);
    }

    const styleAttr = styleParts.length > 0 ? ` style="${styleParts.join('; ')}"` : '';
    const dataAttr = ` data-badge-key="${badgeKey}"`;

    return { style: styleAttr, data: dataAttr, colors };
}

/**
 * Create badge update function for dynamic badge updates
 * @param {Object} bannerDisplay - Banner display settings
 * @returns {Function} Update function
 */
export function createBadgeUpdateFunction(bannerDisplay) {
    return function updateCardBadge(card) {
        const banner = document.getElementById('spots-cards-banner');
        if (!banner) return;

        const badgeElement = banner.querySelector('.cards-badge');
        if (!badgeElement) return;

        const settings = window.currentBannerDisplay || bannerDisplay;
        if (!settings.SHOW_BADGE || !card.badge) {
            badgeElement.style.display = 'none';
            return;
        }

        const badgeContent = getBadgeHtmlForCard(card, settings);
        const badgeKey = card.badge ? String(card.badge).trim().toLowerCase() : null;
        const colors = badgeKey ? getBadgeColors(badgeKey) : null;

        // Update badge content
        badgeElement.innerHTML = badgeContent;

        // Apply colors if configured
        if (colors) {
            if (colors.backgroundColor) {
                badgeElement.style.backgroundColor = colors.backgroundColor;
            }
            if (colors.textColor) {
                badgeElement.style.color = colors.textColor;
                // Update icon color to match text color
                const icon = badgeElement.querySelector('.cards-badge-icon');
                if (icon) {
                    // For SVG images, use filter to match text color
                    if (colors.textColor === 'white' || colors.textColor === '#ffffff' || colors.textColor === 'rgb(255, 255, 255)') {
                        icon.style.filter = 'brightness(0) invert(1)'; // White icon
                    } else {
                        icon.style.filter = 'brightness(0)'; // Black icon
                    }
                }
            }
        } else {
            // Reset to defaults if no colors
            badgeElement.style.backgroundColor = '';
            badgeElement.style.color = '';
            const icon = badgeElement.querySelector('.cards-badge-icon');
            if (icon) {
                icon.style.filter = '';
            }
        }

        badgeElement.style.display = '';
    };
}

// ===== TAB DESIGN FUNCTIONS =====

/**
 * Render tabs HTML
 * @param {Array} tabs - Array of tab objects with {id, label}
 * @param {string|null} activeId - Active tab ID
 * @param {string} onclickHandler - Click handler function name
 * @param {string} distanceHtml - Optional distance HTML to append to tab label
 * @returns {string} Tabs HTML
 */
export function renderTabs(tabs = [], activeId = null, onclickHandler = '__cardsTabSelect', distanceHtml = '') {
    if (!tabs || tabs.length === 0) return '';
    const hasHandler = Boolean(onclickHandler);
    const buttons = tabs.map(tab => {
        const isActive = String(tab.id) === String(activeId);
        const clickable = hasHandler && tabs.length > 1;
        const handler = clickable ? `${onclickHandler}('${tab.id}')` : '';
        const disabledClass = clickable ? '' : ' disabled';
        const rawLabel = tab.label || '';
        const trimmedLabel = rawLabel.includes('|') ? rawLabel.split('|')[0].trim() : rawLabel;
        const primaryLabel = (tab.primaryLabel || trimmedLabel || '').toString();
        const secondaryLabel = (tab.secondaryLabel || '').toString();
        const uppercasePrimary = primaryLabel.toUpperCase();
        const uppercaseSecondary = secondaryLabel.toUpperCase();
        const iconId = tab.icon ? String(tab.icon).trim() : '';

        let labelHtml = '';
        if (secondaryLabel) {
            const separatorHtml = (CARDS_HEADER_USE_TAG_ICON && iconId)
                ? `<span class="cards-tab-divider-icon">${createIconMarkup(iconId, 'cards-tab-icon')}</span>`
                : '<span class="cards-tab-divider-dot">·</span>';
            labelHtml = `${uppercasePrimary}${separatorHtml}${uppercaseSecondary}`;
        } else {
            labelHtml = uppercasePrimary;
        }
        // Add 'long-text' class if text is longer than 30 characters (for mobile font size reduction)
        const longTextClass = (uppercasePrimary + uppercaseSecondary).length > 30 ? ' long-text' : '';
        // Integrate distance HTML into the button (only for active tab or if single tab)
        const shouldShowDistance = (isActive || tabs.length === 1) && distanceHtml;
        const buttonContent = shouldShowDistance ? `${labelHtml}${distanceHtml}` : labelHtml;
        return `<button class="cards-tab ${isActive ? 'active' : ''}${disabledClass}${longTextClass}" ${handler ? `onclick="${handler}"` : ''}>${buttonContent}</button>`;
    }).join('');
    return `<div class="cards-tabs-row">${buttons}</div>`;
}

// ===== CONTAINER HTML BUILDING =====

/**
 * Build container HTML with banner, badge, tabs, and extra content
 * @param {string} bannerHtml - Banner/carousel HTML
 * @param {string} badgeHtml - Badge HTML
 * @param {Object} bannerDisplay - Banner display settings
 * @param {string} extraRowContent - Extra row content
 * @param {string} tabsHtml - Tabs HTML
 * @param {string|null} badgeKey - Badge key for color lookup
 * @returns {string} Complete container HTML
 */
export function buildContainerHtml(bannerHtml, badgeHtml, bannerDisplay, extraRowContent = '', tabsHtml = '', badgeKey = null, actionButtonsHtml = '', headerLeftSuffixHtml = '') {
    // SOURCE OF TRUTH: Use Map Animation Settings + extra cards duration from settings
    const extraFade = MAP_ANIMATION_SETTINGS?.EXTRA_FADE_FOR_CARDS || 0;
    const duration = (MAP_ANIMATION_SETTINGS?.FADE_DURATION_MS || 300) + extraFade;
    // REFACTORED: Just hide via CSS (.show class removal) - don't clear innerHTML or set display:none
    // Container is always in DOM, only visibility changes
    const closeHandler = `(function(e){const b=document.getElementById('spots-cards-banner');if(b){b.classList.remove('show');}e.preventDefault();e.stopPropagation();})(event);`;

    // Get color attributes for the badge
    const colorAttrs = badgeKey ? getBadgeColorAttributes(badgeKey) : { style: '', data: '', colors: null };

    // Add icon filter style if text color is white
    let iconFilterStyle = '';
    if (colorAttrs.colors && colorAttrs.colors.textColor) {
        const textColor = colorAttrs.colors.textColor.toLowerCase();
        if (textColor === 'white' || textColor === '#ffffff' || textColor === 'rgb(255, 255, 255)') {
            iconFilterStyle = ' style="filter: brightness(0) invert(1);"';
        } else {
            iconFilterStyle = ' style="filter: brightness(0);"';
        }
    }

    // Apply icon filter to badge content if needed
    let processedBadgeHtml = badgeHtml;
    if (iconFilterStyle && badgeHtml && badgeHtml.includes('cards-badge-icon')) {
        processedBadgeHtml = badgeHtml.replace(/<img([^>]*class="[^"]*cards-badge-icon[^"]*"[^>]*)>/g, (match, attrs) => {
            if (!attrs.includes('style=')) {
                return `<img${attrs}${iconFilterStyle}>`;
            }
            return match;
        });
    }

    // Always wrap badge content in cards-badge span if not already wrapped
    let normalizedBadge = '';
    if (processedBadgeHtml) {
        if (processedBadgeHtml.trim().startsWith('<span class="cards-badge">')) {
            // Already wrapped, just add color attributes
            normalizedBadge = processedBadgeHtml.replace('<span class="cards-badge">', `<span class="cards-badge"${colorAttrs.style}${colorAttrs.data}>`);
        } else {
            // Wrap with colors
            normalizedBadge = `<span class="cards-badge"${colorAttrs.style}${colorAttrs.data}>${processedBadgeHtml}</span>`;
        }
    }

    // Group tabs into header bar (badge moved inline with description) - tabs left, actions right
    // Integrate distance into tabs instead of separate element
    const topBarContent = `
        <div class="cards-top-left">${tabsHtml || ''}</div>
        <div class="cards-top-right">${actionButtonsHtml || ''}</div>
    `;

    // Add center-content class when setting enabled and no action buttons
    const centerClass = (CARDS_HEADER_CENTER_TABS && !actionButtonsHtml) ? ' center-content' : '';
    const topHeader = topBarContent.trim() ? `<div class="cards-top-header${centerClass}">${topBarContent}</div>` : '';

    // Store normalized badge in window for access by renderCardContent
    window.__cardsInlineBadgeHtml = normalizedBadge || '';

    return `
        <div class="cards-banner-container">
            ${bannerDisplay.SHOW_CLOSE_BUTTON ? `<button class="cards-close-button" onclick="${closeHandler}" aria-label="Close">×</button>` : ''}
            ${topHeader}
            <div class="cards-banner-content">
                ${bannerHtml}
            </div>
            ${extraRowContent ? `<div class="cards-extra-row">${extraRowContent}</div>` : ''}
        </div>
    `;
}


